import type { RefOrGetter } from "ls/common/types";
import type { UserProfile } from "ls/features/user/utils";
import type { ComputedRef, Ref } from "vue";
import type { SectionSuggestion } from "./types";
import type { ShortcutJs, UserProfileJs } from "@/generated/models";
import { createInjectionState } from "@vueuse/core";
import { useContextOrThrow } from "ls/composables/useContextOrThrow";
import { fromUserProfileJs, fromUserSuggestion } from "ls/features/user/utils";
import { computed, readonly, ref, toValue } from "vue";
import { useProvideSuggestionContext } from "../suggestions";
import { useUserSuggestions } from "./queries";

export type SuggestionProfile = UserProfile & { id: string };

const [useProvideMentionSuggestionContext, useMentionSuggestionContextFn] = createInjectionState(() => {
  const userSearchResults = ref<SuggestionProfile[]>([]);
  const sectionSearchResults = ref<SectionSuggestion[]>([]);

  const searchResults = computed(() => {
    return [
      ...userSearchResults.value,
      ...sectionSearchResults.value,
    ];
  });

  const suggestionContext = useProvideSuggestionContext(searchResults);

  function setUserSearchResults(userSearch: SuggestionProfile[]) {
    userSearchResults.value = userSearch;
  }

  function setSectionSearchResults(sectionSearch: SectionSuggestion[]) {
    sectionSearchResults.value = sectionSearch;
  }

  return {
    setUserSearchResults,
    setSectionSearchResults,
    userSearchResults: readonly(userSearchResults),
    sectionSearchResults: readonly(sectionSearchResults),
    ...suggestionContext,
  };
});

export { useProvideMentionSuggestionContext };
export const useMentionSuggestionContext = useContextOrThrow(useMentionSuggestionContextFn);

function useProfilesArray(profilesMap: Ref<Map<string, UserProfile>>) {
  return computed(() => {
    return Array.from(profilesMap.value.entries()).map(([id, profile]) => {
      return {
        id,
        ...profile,
      } satisfies SuggestionProfile;
    });
  });
}

function createProfilesMap<T>(items: (T[] | undefined), getId: (u: T) => string, getProfile: (u: T) => UserProfile) {
  const map = new Map<string, UserProfile>();
  if (!items) return map;

  for (const item of items) {
    map.set(getId(item), getProfile(item));
  }
  return map;
}

// this context is used to get actual nickname when rendering @mention items in rich text
const [useProvideProfilesContext, useProfilesContextFn] = createInjectionState((map: ComputedRef<Record<string, UserProfileJs>>) => {
  const profilesMap = computed(() => {
    const items = Object.entries(toValue(map));
    return createProfilesMap(items, ([id]) => id, ([, user]) => fromUserProfileJs(user));
  });

  const profiles = useProfilesArray(profilesMap);

  return {
    profiles,
    profilesMap,
  };
});

export { useProvideProfilesContext };
export const useProfilesContext = useContextOrThrow(useProfilesContextFn);
const useProfilesContextOptional = useProfilesContextFn;

// the following context is used to provide user suggestions in @mention dropdown
const [useProvideUserSuggestionsContext, useUserSuggestionsContextFn] = createInjectionState((file: RefOrGetter<ShortcutJs>) => {
  const shortcut = computed(() => toValue(file).Shortcut);

  const { data: userSuggestions } = useUserSuggestions(shortcut);

  const profilesMap = computed(() => {
    const items = userSuggestions.value?.Suggestions;
    return createProfilesMap(items, u => u.UserId, u => fromUserSuggestion(u));
  });

  const profiles = useProfilesArray(profilesMap);

  return {
    profiles,
    profilesMap,
  };
});

export { useProvideUserSuggestionsContext };
export const useUserSuggestionsContext = useContextOrThrow(useUserSuggestionsContextFn);
const useUserSuggestionsContextOptional = useUserSuggestionsContextFn;

export function useMentionedUsersProfiles() {
  const profilesContext = useProfilesContextOptional();
  const profilesSuggestionContext = useUserSuggestionsContextOptional();

  const profilesMap = computed(() => {
    const suggestions = profilesSuggestionContext?.profilesMap.value;
    const profiles = profilesContext?.profilesMap.value;
    if (!suggestions || !profiles) {
      return new Map<string, UserProfile>();
    }

    if (suggestions && profiles) {
      // the last one wins in case of duplicates
      return new Map([...profiles, ...suggestions]);
    }

    return suggestions ?? profiles;
  });

  return { profilesMap };
}
